import { useState, useEffect } from 'react'
import axios from 'axios'

const Sponsors = () => {
  const [sponsors, setSponsors] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedSponsor, setSelectedSponsor] = useState(null)

  const tierConfig = {
    gold: {
      label: 'Gold Sponsors',
      price: '$2,000',
      description: 'Prime banner placement, 4th of July naming rights, all-event recognition, logo on site & emails, promotional table opportunity, and first right of renewal.',
      gradient: 'from-yellow-600 to-yellow-700',
      border: 'border-yellow-400',
      bg: 'bg-yellow-50',
      badge: 'bg-yellow-100 text-yellow-800',
      accent: 'text-yellow-700',
    },
    silver: {
      label: 'Silver Sponsors',
      price: '$1,000',
      description: 'Secondary banner placement, 1-2 event sponsorships, logo on site, 1-2 email mentions, and branded giveaway opportunities.',
      gradient: 'from-gray-400 to-gray-500',
      border: 'border-gray-400',
      bg: 'bg-gray-50',
      badge: 'bg-gray-200 text-gray-700',
      accent: 'text-gray-600',
    },
    bronze: {
      label: 'Bronze Sponsors',
      price: '$250',
      description: 'Banner at the pool, name and logo on the website, and thank-you signage at events.',
      gradient: 'from-orange-600 to-orange-700',
      border: 'border-orange-400',
      bg: 'bg-orange-50',
      badge: 'bg-orange-100 text-orange-800',
      accent: 'text-orange-700',
    },
  }

  const tiers = ['gold', 'silver', 'bronze']

  // Convert Google Drive URL to reliable lh3 embed format
  const getDriveImageUrl = (sponsor) => {
    if (sponsor.logoDriveFileId) {
      return `https://lh3.googleusercontent.com/d/${sponsor.logoDriveFileId}`
    }
    return sponsor.logoImageUrl
  }

  // Fetch sponsors
  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await axios.get('/api/sponsors')
        setSponsors(response.data)
      } catch (err) {
        console.error('Error fetching sponsors:', err)
        setError('Failed to load sponsors. Please try again later.')
      } finally {
        setLoading(false)
      }
    }
    fetchSponsors()
  }, [])

  // Group sponsors by tier
  const sponsorsByTier = tiers.reduce((acc, tier) => {
    acc[tier] = sponsors.filter(s => s.tier === tier)
    return acc
  }, {})

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section
        className="relative text-white min-h-[450px] flex items-center"
        style={{ backgroundImage: 'url(/images/club/image-b032a72c.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 via-secondary-800/70 to-tertiary-900/80"></div>
        <div className="container-custom relative z-10 py-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 animate-fade-in text-4xl md:text-5xl lg:text-6xl font-bold">
              Our Sponsors
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-95">
              Thank you to the businesses that support the Lockridge Forest Swim & Tennis Club community
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container-custom py-12">
        {/* Intro */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-lg text-gray-700 leading-relaxed">
            Our sponsorship program connects local businesses with the Lockridge Forest community.
            Sponsors enjoy visibility at club events, on our website, and in member communications
            while supporting the activities and facilities our families love.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-600 text-lg">{error}</p>
          </div>
        ) : (
          <div className="space-y-16">
            {tiers.map(tier => {
              const config = tierConfig[tier]
              const tierSponsors = sponsorsByTier[tier]

              return (
                <section key={tier}>
                  {/* Tier Header */}
                  <div className="text-center mb-8">
                    <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold ${config.badge} mb-3`}>
                      {config.price}
                    </span>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">{config.label}</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">{config.description}</p>
                  </div>

                  {/* Sponsor Cards */}
                  {tierSponsors.length > 0 ? (
                    <div className="flex flex-wrap justify-center gap-6">
                      {tierSponsors.map(sponsor => (
                        <button
                          key={sponsor._id}
                          onClick={() => setSelectedSponsor(sponsor)}
                          className={`bg-white rounded-xl shadow-md border-2 ${config.border} p-6 hover:shadow-xl transition-all duration-300 text-left cursor-pointer group w-full ${tier === 'gold' ? 'sm:w-80' : 'sm:w-72'}`}
                        >
                          {/* Logo */}
                          {sponsor.logoImageUrl ? (
                            <div className="flex justify-center mb-4">
                              <img
                                src={getDriveImageUrl(sponsor)}
                                alt={`${sponsor.companyName} logo`}
                                className={`object-contain rounded ${tier === 'gold' ? 'max-h-32' : 'max-h-24'} max-w-full`}
                              />
                            </div>
                          ) : (
                            <div className={`flex justify-center items-center mb-4 ${tier === 'gold' ? 'h-32' : 'h-24'} ${config.bg} rounded-lg`}>
                              <svg className={`w-12 h-12 ${config.accent} opacity-40`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            </div>
                          )}

                          {/* Company Name */}
                          <h3 className={`font-bold text-gray-900 text-center group-hover:text-primary-600 transition-colors ${tier === 'gold' ? 'text-xl' : 'text-lg'}`}>
                            {sponsor.companyName}
                          </h3>

                          {/* Click hint */}
                          <p className="text-xs text-gray-400 text-center mt-2">Click for details</p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-center py-10 ${config.bg} rounded-xl border-2 border-dashed ${config.border}`}>
                      <svg className={`mx-auto h-10 w-10 ${config.accent} opacity-50 mb-3`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <p className={`text-lg font-medium ${config.accent}`}>
                        Sponsorship opportunities available
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        Become a {tier} sponsor and support our community
                      </p>
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        )}

        {/* CTA Section */}
        {!loading && !error && (
          <div className="mt-16 bg-primary-50 border-l-4 border-primary-600 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-primary-900 mb-3">
              Interested in Sponsoring?
            </h3>
            <p className="text-primary-800 mb-6 text-lg">
              Partner with Lockridge Forest Swim & Tennis Club and connect your business with our
              active community of families. Sponsorships help fund events, maintain facilities, and
              build a stronger neighborhood.
            </p>
            <a
              href="/contact"
              className="btn btn-primary inline-block"
            >
              Contact Us to Learn More
            </a>
          </div>
        )}
      </div>

      {/* Sponsor Detail Lightbox */}
      {selectedSponsor && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedSponsor(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header with tier color */}
            <div className={`bg-gradient-to-r ${tierConfig[selectedSponsor.tier]?.gradient || 'from-gray-500 to-gray-600'} p-4 flex justify-between items-center`}>
              <span className="text-white font-semibold text-lg">
                {tierConfig[selectedSponsor.tier]?.label.replace('Sponsors', 'Sponsor') || 'Sponsor'}
              </span>
              <button
                onClick={() => setSelectedSponsor(null)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {/* Logo */}
              {selectedSponsor.logoImageUrl && (
                <div className="flex justify-center mb-5">
                  <img
                    src={getDriveImageUrl(selectedSponsor)}
                    alt={`${selectedSponsor.companyName} logo`}
                    className="max-h-36 max-w-full object-contain"
                  />
                </div>
              )}

              {/* Company Name */}
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-5">
                {selectedSponsor.companyName}
              </h3>

              {/* Contact Details */}
              <div className="space-y-3">
                {selectedSponsor.address && (
                  <div className="flex items-start text-gray-700">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{selectedSponsor.address}</span>
                  </div>
                )}

                {selectedSponsor.email && (
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <a href={`mailto:${selectedSponsor.email}`} className="text-primary-600 hover:text-primary-800 hover:underline">
                      {selectedSponsor.email}
                    </a>
                  </div>
                )}

                {selectedSponsor.phone && (
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <a href={`tel:${selectedSponsor.phone.replace(/[^\d+]/g, '')}`} className="text-primary-600 hover:text-primary-800 hover:underline">
                      {selectedSponsor.phone}
                    </a>
                  </div>
                )}

                {selectedSponsor.website && (
                  <div className="flex items-center text-gray-700">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <a
                      href={selectedSponsor.website.startsWith('http') ? selectedSponsor.website : `https://${selectedSponsor.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800 hover:underline"
                    >
                      {selectedSponsor.website}
                    </a>
                  </div>
                )}

                {!selectedSponsor.address && !selectedSponsor.email && !selectedSponsor.phone && !selectedSponsor.website && (
                  <p className="text-gray-500 text-center italic">No contact information available.</p>
                )}
              </div>

              {/* Close button */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => setSelectedSponsor(null)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Sponsors
